import { useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { mutate } from 'swr';
import { Cart, DiscountErrorCode } from 'shared/types/cart';
import {
  AlertCode,
  PrepareCartForProgramPayload,
  PrepareCartPayload,
  PrepareCartResponse,
} from 'shared/types/checkout';
import {
  getProductFromLineItem,
  getProductsFromLineItems,
} from 'helpers/get-products-from-line-items';
import { makeSDKCallWithRetries } from 'helpers/makeSDKCallWithRetries';
import {
  centsToDollars,
  getPriceDetailsFromCart,
  sumMoney,
} from 'helpers/priceHelpers';
import { sendGTMEvent } from 'helpers/sendGTMEvent';
import { loginRedirectUrl, withCookieValidation } from 'helpers/utils/cookie';
import { sdk } from 'sdk';
import { getAccountId, setAlert, useAppDispatch, useAppSelector } from 'store';
import { useClearCart } from '../useClearCart';
import { redirectToLegacyEcommerce } from './helpers';

const CHECKOUT_PAGE_PATH = '/checkout';

export const usePrepareCart = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const { replace } = useRouter();
  const searchParams = useSearchParams();
  const { clearCart } = useClearCart();

  // This function is used to prepare the cart with the given product.
  const prepareCart = useCallback(
    async ({
      courseRunKeys,
      skus,
      discountCode,
      programId,
    }: {
      courseRunKeys?: string[];
      skus: string[];
      discountCode?: string;
      programId?: string;
    }) => {
      let apiResponse;

      if (programId) {
        const payload: PrepareCartForProgramPayload = {
          discountCode,
          programId,
          legacySkus: skus,
        };
        apiResponse = await withCookieValidation(() =>
          makeSDKCallWithRetries('cart/prepareCartForProgram', customerId, () =>
            sdk.callAction<PrepareCartResponse>({
              actionName: 'cart/prepareCartForProgram',
              payload,
            }),
          ),
        );
      } else {
        const payload: PrepareCartPayload = {
          variant: { skus: courseRunKeys ?? [], legacySkus: skus },
          discountCode,
        };
        apiResponse = await withCookieValidation(() =>
          makeSDKCallWithRetries('cart/prepareCart', customerId, () =>
            sdk.callAction<PrepareCartResponse>({
              actionName: 'cart/prepareCart',
              payload,
            }),
          ),
        );
      }

      console.info(`Response for prepareCart for customer: ${customerId}`, {
        tracing: apiResponse.tracing,
        ...(apiResponse.isError
          ? {
              isError: true,
              error: {
                code: (apiResponse.error.code as string) ?? 'FetchError',
                message: apiResponse.error.message,
              },
            }
          : apiResponse.data.isError
            ? {
                isError: true,
                error: {
                  code: apiResponse.data.error.code,
                  message: apiResponse.data.error.message,
                },
              }
            : {
                isError: false,
                cartID: apiResponse.data.cart.cartId,
                discountErrorCode: apiResponse.data.discountErrorCode,
              }),
      });

      // This code checks whether there is an unhandled exception
      // or timeout that occurred during an extension call.
      if (apiResponse.isError) {
        return apiResponse;
      }

      // This code checks whether there is a handled exception
      // that occurred during an extension call.
      if (apiResponse.data.isError) {
        return apiResponse.data;
      }

      const { cart, discountErrorCode } = apiResponse.data;
      const {
        cartId,
        discountCodes,
        discountOnTotalPrice,
        lineItems,
        sum,
        discountOnLineItems,
      } = cart;

      lineItems.forEach((lineItem) => {
        sendGTMEvent({
          event: 'Product Added',
          properties: {
            track_plan_id: 28,
            trigger_source: 'client-side',
            cart_id: cartId,
            checkout_id: cartId,
            coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
            currency: sum.currencyCode ?? 'USD',
            ...getProductFromLineItem(lineItem),
          },
        });
      });

      const formattedResponse = {
        data: cart,
      };

      if (discountErrorCode) {
        await mutate('/action/cart/getCart');
      } else {
        await mutate('/action/cart/getCart', formattedResponse, {
          optimisticData: formattedResponse,
          revalidate: false,
          populateCache: true,
        });
      }

      if (discountErrorCode) {
        dispatch(
          setAlert({
            code: discountErrorCode,
            value: discountCode,
          }),
        );
      } else if (discountCode) {
        sendGTMEvent({
          event: 'Coupon Applied',
          properties: {
            track_plan_id: 30,
            trigger_source: 'client-side',
            cart_id: cartId,
            checkout_id: cartId,
            coupon_name: discountCodes[discountCodes.length - 1]?.code,
            discount: centsToDollars(
              sumMoney(discountOnTotalPrice, discountOnLineItems),
            ),
            currency: discountOnTotalPrice?.currencyCode ?? 'USD',
            products: getProductsFromLineItems(lineItems),
          },
        });
      }

      return apiResponse;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const prepareCartForCourses = useCallback(
    async (courseRunKeys: string[], skus: string[], discountCode?: string) => {
      if (courseRunKeys.length > 0) {
        const result = await prepareCart({ courseRunKeys, skus, discountCode });
        if (!result || result.isError) {
          // The sku mentioned below is Merchant Center sku which will be one of the failed course run key

          const itemIdentification = result?.error?.itemIdentification as {
            sku: string;
          };
          const action = result?.error?.action as { sku: string };
          const invalidSku = itemIdentification?.sku || action?.sku;

          console.error(
            `Error initializing cart with product(s) for customer: ${customerId} with error code: ${
              (result?.error?.code as string) ?? ''
            } and error message: ${
              result?.error?.message ??
              JSON.stringify(result?.error) ??
              'Unknown'
            } for sku: ${invalidSku ?? courseRunKeys.join(', ')}`,
          );

          if (
            result?.error?.code === 'InvalidInput' &&
            result?.error?.message.startsWith('LineItem error: ')
          ) {
            console.error(
              `Product not found for course_run_key: ${invalidSku} and sku(s): ${skus.join(
                ', ',
              )} for customer: ${customerId}`,
            );
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_NOT_FOUND,
                value: invalidSku,
              }),
            );
          } else if (
            (result?.error?.code as string)?.includes('authentication_error') ||
            result?.error?.message?.includes('TokenExpiredError')
          ) {
            // Redirect to login page if the token is expired
            window.location.replace(loginRedirectUrl());
          } else if (
            result?.error?.code === DiscountErrorCode.DOES_NOT_EXIST ||
            result?.error?.code ===
              DiscountErrorCode.DISCOUNT_CODE_NON_APPLICABLE
          ) {
            console.info(
              `Coupon not found in CT during auto-application, redirecting to legacy e-commerce for coupon: ${
                discountCode ?? ''
              } and customer: ${customerId} and product ID: ${courseRunKeys.join(
                ', ',
              )} being purchased`,
            );
            await clearCart();
            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            const params = {
              path,
              skus,
              ...(discountCode && { discountCode }),
            };

            redirectToLegacyEcommerce(params);
            return;
          } else if (
            result?.error?.code === AlertCode.PRODUCT_CREDIT_ELIGIBILITY_ERROR
          ) {
            console.error(result?.error?.message);
            await clearCart();
            dispatch(
              setAlert({
                code: AlertCode.PRODUCT_CREDIT_ELIGIBILITY_ERROR,
              }),
            );
          } else if (
            result?.error?.code ===
            AlertCode.PRODUCT_ENTITLEMENTS_ENROLLEMENT_FETCH_ERROR
          ) {
            console.error(
              `Error while fetching the enrollment or entitlement for course_run_key(s): ${courseRunKeys.join(
                ', ',
              )} and sku(s): ${skus.join(', ')}. that the customer: ${customerId} is trying to purchase`,
            );
            await clearCart();

            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            const params = {
              path,
              skus,
              ...(discountCode && { discountCode }),
            };
            redirectToLegacyEcommerce(params);
          } else if (
            result?.error?.code === AlertCode.PRODUCT_UNAVAILABLE_TO_BUY
          ) {
            console.error(
              `Product(s) with course_run_key(s): ${courseRunKeys.join(
                ', ',
              )} that the customer: ${customerId} is trying to purchase have expired.`,
            );
            dispatch(setAlert({ code: AlertCode.PRODUCT_UNAVAILABLE_TO_BUY }));
          } else {
            console.error(
              `Error initializing cart with course_run_key(s): ${courseRunKeys.join(
                ', ',
              )} and sku(s): ${skus.join(', ')}. ` +
                `Redirecting to legacy e-commerce for customer: ${customerId}`,
            );
            await clearCart();

            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            redirectToLegacyEcommerce({ path, skus });
            return;
          }
        } else {
          // Handle successful cart preparation and GTM event
          const { cart } = result.data as { cart: Cart };

          const { cartId, discountCodes, lineItems, sum } = cart;

          const { lineItemsTaxed, discountInDollars } =
            getPriceDetailsFromCart(cart);

          sendGTMEvent({
            event: 'Checkout Started',
            properties: {
              track_plan_id: 29,
              trigger_source: 'client-side',
              cart_id: cartId,
              checkout_id: cartId,
              currency: sum.currencyCode ?? 'USD',
              revenue: centsToDollars(lineItemsTaxed.amount),
              coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
              value: centsToDollars(lineItemsTaxed.grossAmount),
              discount: discountInDollars,
              products: getProductsFromLineItems(lineItems),
            },
          });
        }
      } else {
        if (courseRunKeys.length === 0) {
          console.error(
            `Missing course_run_key(s) which is required to add a course to the cart for customer: ${customerId}`,
          );
        }
        await clearCart();
        dispatch(setAlert({ code: AlertCode.SKU_NOT_PROVIDED }));
      }

      // Build checkout URL
      const urlParams = new URLSearchParams();
      skus.forEach((sku) => urlParams.append('sku', sku));

      // Preserve correlationId parameter if it exists
      const correlationId = searchParams.get('correlationId');
      if (correlationId) {
        urlParams.set('correlationId', correlationId);
      }

      replace(`${CHECKOUT_PAGE_PATH}/?${urlParams.toString()}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const prepareCartForProgram = useCallback(
    async (programId: string, skus: string[], discountCode?: string) => {
      if (programId && skus.length > 0) {
        const result = await prepareCart({ skus, discountCode, programId });
        if (!result || result.isError) {
          console.error(
            `Error initializing cart with program ID: ${programId} for customer: ${customerId} with error code: ${
              (result?.error?.code as string) ?? ''
            } and error message: ${
              result?.error?.message ??
              JSON.stringify(result?.error) ??
              'Unknown'
            }`,
          );

          if (
            (result?.error?.code as string)?.includes('authentication_error') ||
            result?.error?.message?.includes('TokenExpiredError')
          ) {
            // Redirect to login page if the token is expired
            window.location.replace(loginRedirectUrl());
          } else if (
            result?.error?.code === DiscountErrorCode.DOES_NOT_EXIST ||
            result.error?.code ===
              DiscountErrorCode.DISCOUNT_CODE_NON_APPLICABLE
          ) {
            console.info(
              `Coupon not found in CT during auto-application, redirecting to legacy e-commerce for coupon: ${
                discountCode ?? ''
              } and customer: ${customerId} and product ID: ${programId} being purchased`,
            );
            await clearCart();
            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            redirectToLegacyEcommerce({
              path,
              discountCode,
              skus,
              bundle: programId,
            });
            return;
          } else if (result?.error?.code === AlertCode.ORDER_ALREADY_PLACED) {
            console.error(
              `Customer ${customerId} has already purchased the product for program ID: ${programId}.`,
            );
            dispatch(setAlert({ code: AlertCode.ORDER_ALREADY_PLACED }));
          } else if (result?.error?.code === AlertCode.PRODUCT_NOT_FOUND) {
            console.error(
              `Product not found for program ID: ${programId}
            and sku(s): ${skus.join(', ')}. that the customer: ${customerId} is trying to purchase does not exist`,
            );
            dispatch(
              setAlert({ code: AlertCode.PRODUCT_NOT_FOUND, value: programId }),
            );
          } else if (
            result?.error?.code ===
            AlertCode.PRODUCT_ENTITLEMENTS_ENROLLEMENT_FETCH_ERROR
          ) {
            console.error(
              `Error while fetching the enrollment or entitlement for program ID: ${programId}
            and sku(s): ${skus.join(', ')}. that the customer: ${customerId} is trying to purchase`,
            );
            await clearCart();

            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            const params = {
              path,
              skus,
              bundle: programId,
              ...(discountCode && { discountCode }),
            };
            redirectToLegacyEcommerce(params);
          } else {
            console.error(
              `Error initializing cart with program ID: ${programId}  and sku(s): ${skus.join(', ')}. ` +
                `Redirecting to legacy e-commerce for customer: ${customerId}`,
            );
            await clearCart();

            const path = process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
            redirectToLegacyEcommerce({ path, skus, bundle: programId });
            return;
          }
        } else {
          // Handle successful cart preparation
          const { cart } = result.data as { cart: Cart };

          const { cartId, discountCodes, lineItems, sum } = cart;

          const { lineItemsTaxed, discountInDollars } =
            getPriceDetailsFromCart(cart);

          sendGTMEvent({
            event: 'Checkout Started',
            properties: {
              track_plan_id: 29,
              trigger_source: 'client-side',
              cart_id: cartId,
              checkout_id: cartId,
              currency: sum.currencyCode ?? 'USD',
              revenue: centsToDollars(lineItemsTaxed.amount),
              coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
              value: centsToDollars(lineItemsTaxed.grossAmount),
              discount: discountInDollars,
              products: getProductsFromLineItems(lineItems),
            },
          });
        }
      } else {
        if (!programId) {
          console.error(`Missing program ID for customer: ${customerId}`);
        }
        if (skus.length === 0) {
          console.error(
            `Missing sku(s) which is required to add a course to the cart for customer: ${customerId}`,
          );
        }
        await clearCart();
        dispatch(setAlert({ code: AlertCode.SKU_NOT_PROVIDED }));
      }
      // Build checkout URL with program ID
      const urlParams = new URLSearchParams();
      skus.forEach((sku) => urlParams.append('sku', sku));
      urlParams.set('bundle', programId);

      // Preserve correlationId parameter if it exists
      const correlationId = searchParams.get('correlationId');
      if (correlationId) {
        urlParams.set('correlationId', correlationId);
      }

      replace(`${CHECKOUT_PAGE_PATH}/?${urlParams.toString()}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  return {
    prepareCartForCourses,
    prepareCartForProgram,
  };
};
